import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/api/client";
import { Card, SectionLabel } from "@/components/ui/Card";
import { BackButton } from "@/components/ui/BackButton";
import { fmtDate } from "@/lib/dates";

type Tab = "week" | "month";

interface DayRow {
  date: string;
  meal_count: number;
  intake_kcal: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  fiber_g: number;
  training_burn_kcal: number;
  target_kcal: number;
  target_protein_g: number;
  deviation_kcal: number | null;
  adherence_pct: number | null;
}

interface WeekSummary {
  week_start: string;
  week_end: string;
  logged_days: number;
  complete_days?: number;
  missing_days: number;
  logging_completeness_pct?: number;
  averages: {
    intake_kcal: number;
    intake_kcal_per_logged_day?: number;
    intake_kcal_per_week_day?: number | null;
    target_kcal: number;
    protein_g: number;
    protein_g_per_logged_day?: number;
    protein_g_per_week_day?: number;
    carbs_g: number;
    fat_g: number;
    training_burn_kcal: number;
  } | null;
  totals: {
    intake_kcal: number;
    target_kcal: number;
    deviation_kcal: number | null;
    training_burn_kcal: number;
  } | null;
  training_vs_rest: {
    training_days_avg_intake: number | null;
    rest_days_avg_intake: number | null;
    training_days: number;
    rest_days: number;
  } | null;
  adherence: {
    days_within_10pct_target: number;
    days_under_target: number;
    days_over_target: number;
  } | null;
  days: DayRow[];
}

interface MonthSummary {
  year: number;
  month: number;
  month_name: string;
  logged_days: number;
  complete_days?: number;
  expected_days: number;
  completion_pct: number;
  averages: { intake_kcal: number | null; protein_g_per_kg: number | null };
  vs_previous_month: {
    previous_avg_intake_kcal: number | null;
    delta_kcal: number | null;
  };
  weeks: Array<{
    week_start: string;
    week_end: string;
    logged_days: number;
  complete_days?: number;
    avg_intake_kcal: number;
    avg_protein_g: number;
  }>;
  days: DayRow[];
}

function getMonday(d: Date): Date {
  const dt = new Date(d);
  const day = dt.getDay();
  const diff = day === 0 ? -6 : 1 - day; // ISO Monday
  dt.setDate(dt.getDate() + diff);
  dt.setHours(0, 0, 0, 0);
  return dt;
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export default function NutritionArchive() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("week");

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <BackButton to="/nutrition" label={t("nutrition_archive.back_to_nutrition")} withHome={false} />
      <header>
        <SectionLabel>{t("nutrition_archive.section_label")}</SectionLabel>
        <h1 className="mt-1 font-serif text-[26px] font-medium leading-snug md:text-[30px]">
          {t("nutrition_archive.h1")}
        </h1>
      </header>

      <div className="flex gap-1 border-b border-cream-border">
        <TabButton active={tab === "week"} onClick={() => setTab("week")}>
          {t("nutrition_archive.tab_week")}
        </TabButton>
        <TabButton active={tab === "month"} onClick={() => setTab("month")}>
          {t("nutrition_archive.tab_month")}
        </TabButton>
      </div>

      {tab === "week" ? <WeekView /> : <MonthView />}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px border-b-2 px-4 py-2 text-[13px] transition-colors ${
        active
          ? "border-amber font-medium text-navy-deep"
          : "border-transparent text-taupe hover:text-navy-deep"
      }`}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Week view
// ---------------------------------------------------------------------------

function WeekView() {
  const { t } = useTranslation();
  const [weekStart, setWeekStart] = useState<Date>(getMonday(new Date()));
  const startStr = isoDate(weekStart);

  const q = useQuery({
    queryKey: ["nutrition-week", startStr],
    queryFn: async () => {
      const { data } = await api.get<WeekSummary>(
        `/nutrition/archive/week-summary?week_start=${startStr}`,
      );
      return data;
    },
  });

  const shift = (days: number) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + days);
    setWeekStart(getMonday(d));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => shift(-7)}
          className="inline-flex items-center gap-1 rounded-md border border-cream-border bg-cream-light px-3 py-1.5 text-[13px] hover:bg-cream"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> {t("nutrition_archive.previous_week")}
        </button>
        <div className="text-center text-[13px] text-navy-deep">
          {q.data
            ? `${fmtDate(q.data.week_start, "d MMM")} – ${fmtDate(q.data.week_end, "d MMM yyyy")}`
            : "…"}
        </div>
        <button
          type="button"
          onClick={() => shift(7)}
          disabled={isoDate(getMonday(new Date())) === startStr}
          className="inline-flex items-center gap-1 rounded-md border border-cream-border bg-cream-light px-3 py-1.5 text-[13px] hover:bg-cream disabled:opacity-40"
        >
          {t("nutrition_archive.next_week")} <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>

      {q.isLoading ? (
        <div className="py-6 text-center text-taupe">{t("nutrition_archive.loading")}</div>
      ) : !q.data ? (
        <div className="py-6 text-center text-coral">{t("nutrition_archive.load_failed")}</div>
      ) : q.data.logged_days === 0 && !(q.data.complete_days ?? 0) ? (
        <Card>
          <p className="text-[13px] text-taupe">{t("nutrition_archive.no_data_week")}</p>
        </Card>
      ) : (
        <>
          {(q.data.complete_days ?? 0) < 7 ? (
            <div className="rounded-xl border border-amber/40 bg-amber/10 p-4 text-[13px] text-navy-deep">
              {t("diary_quality.coverage", { n: q.data.complete_days ?? 0 })}
            </div>
          ) : null}

          <Card>
            <SectionLabel>{t("nutrition_archive.summary")}</SectionLabel>
            <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
              <Stat
                label={t("diary_quality.avg_confirmed")}
                value={`${q.data.averages?.intake_kcal ?? "—"}`}
                sub={t("nutrition_archive.kcal")}
              />
              <Stat
                label={t("nutrition_archive.avg_per_week_day")}
                value={`${q.data.averages?.intake_kcal_per_week_day ?? "—"}`}
                sub={t("nutrition_archive.kcal_per_day")}
              />
              <Stat
                label={t("diary_quality.confirmed_days")}
                value={`${q.data.complete_days ?? 0}/7`}
                sub={`${q.data.logging_completeness_pct ?? Math.round((q.data.logged_days / 7) * 100)}%`}
              />
              <Stat
                label={t("nutrition_archive.in_target_10pct")}
                value={q.data.adherence ? `${q.data.adherence.days_within_10pct_target}/${q.data.complete_days ?? 0}` : "—"}
                sub={t("nutrition_archive.days")}
              />
            </div>
            <DailyBarChart days={q.data.days} />
            <TrainingVsRest tvr={q.data.training_vs_rest} />
          </Card>

          <Card>
            <SectionLabel>{t("nutrition_archive.days_section")}</SectionLabel>
            <div className="mt-3 space-y-2">
              {q.data.days.map((d) => (
                <DayRowCard key={d.date} day={d} />
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
}: {
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <div>
      <div className="section-label">{label}</div>
      <div className="metric-number mt-0.5 text-[20px] text-navy-deep">{value}</div>
      {sub ? <div className="text-[11px] text-taupe">{sub}</div> : null}
    </div>
  );
}

function DailyBarChart({ days }: { days: DayRow[] }) {
  const { t } = useTranslation();
  // Sort ascending for the chart so left→right reads chronologically.
  const ordered = [...days].sort((a, b) => (a.date < b.date ? -1 : 1));
  const maxKcal = Math.max(
    ...ordered.map((d) => Math.max(d.intake_kcal, d.target_kcal)),
    1,
  );
  return (
    <div className="mt-4">
      <div className="flex items-end gap-1.5" style={{ height: 100 }}>
        {ordered.map((d) => {
          const intakeH = Math.max(2, (d.intake_kcal / maxKcal) * 100);
          const targetH = Math.max(2, (d.target_kcal / maxKcal) * 100);
          const over = d.deviation_kcal != null && d.deviation_kcal > d.target_kcal * 0.1;
          const under = d.deviation_kcal != null && d.deviation_kcal < -d.target_kcal * 0.1;
          return (
            <div
              key={d.date}
              className="relative flex flex-1 items-end justify-center gap-0.5"
              title={`${d.date}: ${d.intake_kcal} / ${d.target_kcal} kcal`}
            >
              <div
                className={`w-2/5 rounded-t ${
                  d.deviation_kcal == null ? "bg-cream-border" : over
                    ? "bg-coral/70"
                    : under
                      ? "bg-amber/40"
                      : "bg-amber/70"
                }`}
                style={{ height: `${intakeH}%` }}
              />
              <div
                className="w-2/5 rounded-t bg-cream-border"
                style={{ height: `${targetH}%` }}
              />
            </div>
          );
        })}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-taupe">
        {ordered.map((d) => (
          <div key={d.date} className="flex-1 text-center">
            {new Date(d.date).toLocaleDateString("ru-RU", { weekday: "short" })}
          </div>
        ))}
      </div>
      <div className="mt-2 flex gap-3 text-[10px] text-taupe">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-sm bg-amber/70" /> {t("nutrition_archive.legend_eaten")}
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-sm bg-cream-border" /> {t("nutrition_archive.legend_target")}
        </span>
      </div>
    </div>
  );
}

function TrainingVsRest({
  tvr,
}: {
  tvr: WeekSummary["training_vs_rest"];
}) {
  const { t } = useTranslation();
  if (!tvr || tvr.training_days_avg_intake == null || tvr.rest_days_avg_intake == null) {
    return null;
  }
  const delta = tvr.training_days_avg_intake - tvr.rest_days_avg_intake;
  const periodised = delta > 200;
  return (
    <div className="mt-4 border-t border-cream-border pt-3">
      <div className="text-[11px] uppercase tracking-wider text-taupe">
        {t("nutrition_archive.training_vs_rest")}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-3 text-[13px]">
        <div>
          <span className="text-taupe">{t("nutrition_archive.training_label")} ({tvr.training_days}):</span>{" "}
          <span className="metric-number text-navy-deep">
            {tvr.training_days_avg_intake}
          </span>{" "}
          {t("nutrition_archive.kcal")}
        </div>
        <div>
          <span className="text-taupe">{t("nutrition_archive.rest_label")} ({tvr.rest_days}):</span>{" "}
          <span className="metric-number text-navy-deep">
            {tvr.rest_days_avg_intake}
          </span>{" "}
          {t("nutrition_archive.kcal")}
        </div>
      </div>
      <div className="mt-1 text-[11px] text-taupe">
        {t("nutrition_archive.diff_kcal", { n: Math.abs(delta) })}{" "}
        {periodised
          ? t("nutrition_archive.periodized_msg")
          : t("nutrition_archive.flat_intake_msg")}
      </div>
    </div>
  );
}

function DayRowCard({ day }: { day: DayRow }) {
  const { t } = useTranslation();
  const over = day.deviation_kcal != null && day.deviation_kcal > day.target_kcal * 0.1;
  const under = day.deviation_kcal != null && day.deviation_kcal < -day.target_kcal * 0.1;
  const dColor = over ? "text-coral" : under ? "text-amber-dark" : "text-navy-deep";
  return (
    <Link
      to={`/nutrition/day/${day.date}`}
      className="flex w-full items-center justify-between rounded-lg border border-cream-border bg-cream-light px-3 py-2.5 text-left hover:bg-cream"
    >
      <div className="flex-1">
        <div className="text-[13px] font-medium text-navy-deep">
          {fmtDate(day.date, "EEEE, d MMM")}
        </div>
        <div className="text-[11px] text-taupe">
          {t("nutrition_archive.n_meals_protein", { meals: day.meal_count, p: day.protein_g, unit: t("nutrition_page.g") })}
          {day.training_burn_kcal > 100
            ? ` · ${t("nutrition_archive.training_kcal_inline", { n: day.training_burn_kcal })}`
            : ""}
        </div>
      </div>
      <div className="text-right">
        <div className={`metric-number text-[14px] ${dColor}`}>
          {day.intake_kcal} / {day.target_kcal}
        </div>
        <div className="text-[11px] text-taupe">
          {day.deviation_kcal != null && day.deviation_kcal > 0 ? "+" : ""}
          {day.deviation_kcal ?? "—"} {t("nutrition_archive.kcal")}
        </div>
      </div>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Month view
// ---------------------------------------------------------------------------

function MonthView() {
  const { t } = useTranslation();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);

  const q = useQuery({
    queryKey: ["nutrition-month", year, month],
    queryFn: async () => {
      const { data } = await api.get<MonthSummary>(
        `/nutrition/archive/month-summary?year=${year}&month=${month}`,
      );
      return data;
    },
  });

  const shift = (delta: number) => {
    let m = month + delta;
    let y = year;
    while (m < 1) {
      m += 12;
      y -= 1;
    }
    while (m > 12) {
      m -= 12;
      y += 1;
    }
    setYear(y);
    setMonth(m);
  };

  const isCurrent = year === today.getFullYear() && month === today.getMonth() + 1;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => shift(-1)}
          className="inline-flex items-center gap-1 rounded-md border border-cream-border bg-cream-light px-3 py-1.5 text-[13px] hover:bg-cream"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> {t("nutrition_archive.previous")}
        </button>
        <div className="text-center text-[13px] text-navy-deep">
          {q.data ? `${q.data.month_name} ${q.data.year}` : `${month}/${year}`}
        </div>
        <button
          type="button"
          onClick={() => shift(1)}
          disabled={isCurrent}
          className="inline-flex items-center gap-1 rounded-md border border-cream-border bg-cream-light px-3 py-1.5 text-[13px] hover:bg-cream disabled:opacity-40"
        >
          {t("nutrition_archive.next")} <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>

      {q.isLoading ? (
        <div className="py-6 text-center text-taupe">{t("nutrition_archive.loading")}</div>
      ) : !q.data ? (
        <div className="py-6 text-center text-coral">{t("nutrition_archive.load_failed")}</div>
      ) : (
        <>
          <Card>
            <SectionLabel>{t("nutrition_archive.month_summary")}</SectionLabel>
            <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
              <Stat
                label={t("diary_quality.confirmed_days")}
                value={`${q.data.complete_days ?? 0}/${q.data.expected_days}`}
                sub={`${q.data.completion_pct}%`}
              />
              <Stat
                label={t("diary_quality.avg_confirmed")}
                value={`${q.data.averages.intake_kcal ?? "—"}`}
                sub={t("nutrition_archive.kcal_per_day")}
              />
              {q.data.averages.protein_g_per_kg != null ? (
                <Stat
                  label={t("nutrition_archive.protein_short")}
                  value={`${q.data.averages.protein_g_per_kg}`}
                  sub={t("nutrition_archive.g_per_kg")}
                />
              ) : null}
              {q.data.vs_previous_month.delta_kcal != null ? (
                <Stat
                  label={t("nutrition_archive.vs_prev_month")}
                  value={`${q.data.vs_previous_month.delta_kcal > 0 ? "+" : ""}${q.data.vs_previous_month.delta_kcal}`}
                  sub={t("nutrition_archive.kcal_per_day")}
                />
              ) : null}
            </div>
          </Card>

          {q.data.weeks.length > 0 ? (
            <Card>
              <SectionLabel>{t("nutrition_archive.by_weeks")}</SectionLabel>
              <div className="mt-3 space-y-2">
                {q.data.weeks.map((w) => (
                  <div
                    key={w.week_start}
                    className="flex items-center justify-between rounded-lg border border-cream-border bg-cream-light px-3 py-2.5"
                  >
                    <div className="text-[13px] text-navy-deep">
                      {fmtDate(w.week_start, "d MMM")} – {fmtDate(w.week_end, "d MMM")}
                    </div>
                    <div className="text-right">
                      <div className="metric-number text-[14px] text-navy-deep">
                        {w.avg_intake_kcal} {t("nutrition_archive.kcal")}
                      </div>
                      <div className="text-[11px] text-taupe">
                        {t("nutrition_archive.n_of_7_protein", { n: w.logged_days, p: w.avg_protein_g, unit: t("nutrition_page.g") })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ) : null}

          <Card>
            <SectionLabel>{t("nutrition_archive.calendar")}</SectionLabel>
            <CalendarGrid year={q.data.year} month={q.data.month} days={q.data.days} />
          </Card>

          {q.data.days.length > 0 ? (
            <Card>
              <SectionLabel>{t("nutrition_archive.all_days_list")}</SectionLabel>
              <div className="mt-3 space-y-2">
                {q.data.days.map((d) => (
                  <DayRowCard key={d.date} day={d} />
                ))}
              </div>
            </Card>
          ) : null}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Calendar grid for the month — colour-coded cells linking to day detail
// ---------------------------------------------------------------------------

function CalendarGrid({
  year,
  month,
  days,
}: {
  year: number;
  month: number;
  days: DayRow[];
}) {
  const { t } = useTranslation();
  const byDate = new Map(days.map((d) => [d.date, d]));
  const firstOfMonth = new Date(year, month - 1, 1);
  const daysInMonth = new Date(year, month, 0).getDate();
  const startCol = (firstOfMonth.getDay() + 6) % 7; // Monday=0

  const cells: Array<{ key: string; day: DayRow | null; dom: number | null }> = [];
  for (let i = 0; i < startCol; i++) {
    cells.push({ key: `lead-${i}`, day: null, dom: null });
  }
  for (let dom = 1; dom <= daysInMonth; dom++) {
    const dt = new Date(year, month - 1, dom);
    const iso = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    cells.push({ key: iso, day: byDate.get(iso) || null, dom });
  }
  while (cells.length % 7 !== 0) {
    cells.push({ key: `tail-${cells.length}`, day: null, dom: null });
  }

  return (
    <div className="mt-3">
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-wider text-taupe">
        {[
          t("nutrition_page.wd_short_mon"),
          t("nutrition_page.wd_short_tue"),
          t("nutrition_page.wd_short_wed"),
          t("nutrition_page.wd_short_thu"),
          t("nutrition_page.wd_short_fri"),
          t("nutrition_page.wd_short_sat"),
          t("nutrition_page.wd_short_sun"),
        ].map((d) => (
          <div key={d} className="py-1">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c) => (
          <CalendarCell key={c.key} dom={c.dom} day={c.day} />
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-3 text-[10px] text-taupe">
        <Legend color="bg-amber/30" label={t("nutrition_archive.legend_in_target")} />
        <Legend color="bg-coral/40" label={t("nutrition_archive.legend_over")} />
        <Legend color="bg-amber/15" label={t("nutrition_archive.legend_under")} />
        <Legend color="bg-cream-border" label={t("nutrition_archive.legend_no_data")} />
      </div>
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`inline-block h-3 w-3 rounded-sm ${color}`} />
      {label}
    </span>
  );
}

function CalendarCell({
  dom,
  day,
}: {
  dom: number | null;
  day: DayRow | null;
}) {
  if (dom == null) {
    return <div className="aspect-square" aria-hidden="true" />;
  }
  if (!day) {
    return (
      <div className="flex aspect-square flex-col rounded-md border border-cream-border bg-cream-border/30 p-1.5 text-taupe/60">
        <span className="text-[11px] font-medium">{dom}</span>
      </div>
    );
  }
  const over = day.deviation_kcal != null && day.deviation_kcal > day.target_kcal * 0.1;
  const under = day.deviation_kcal != null && day.deviation_kcal < -day.target_kcal * 0.1;
  const tone = day.deviation_kcal == null ? "border-cream-border bg-cream-light" : over
    ? "border-coral/40 bg-coral/10"
    : under
      ? "border-amber/30 bg-amber/15"
      : "border-amber/40 bg-amber/30";
  return (
    <Link
      to={`/nutrition/day/${day.date}`}
      className={`flex aspect-square flex-col justify-between rounded-md border p-1.5 transition-shadow hover:shadow-soft ${tone}`}
      title={`${day.intake_kcal} / ${day.target_kcal} kcal`}
    >
      <span className="text-[11px] font-medium text-navy-deep">{dom}</span>
      <div className="text-right">
        <div className="metric-number text-[10px] leading-none text-navy-deep">
          {day.intake_kcal}
        </div>
      </div>
    </Link>
  );
}
